// ============================================================================
// UPCOMING CLEANING TASKS HOOK
// Next tasks for the cleaner portal completion / empty states.
// Must match CleanerDashboard "Upcoming" (useMyTasks "week"): same date window
// and multi-checklist assignments, so we never tease a "next job" the schedule hides.
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { getTaskIdsWithChecklistAssignmentRows } from "@/lib/cleaningTaskChecklistAssignments";
import type { UpcomingCleaningTask } from "@/types/cleaning";

export type { UpcomingCleaningTask };

type TaskRow = {
  id: string;
  property_id: string;
  scheduled_date: string;
  reservation_id?: string | null;
};

function localYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const useUpcomingCleaningTasks = (excludeTaskId?: string) => {
  const { data: cleaner } = useCleanerRecord();

  return useQuery({
    queryKey: ["upcoming-cleaning-tasks", "week-scope", cleaner?.id, excludeTaskId],
    queryFn: async () => {
      if (!cleaner) return [];

      const now = new Date();
      const todayStr = localYmd(now);
      const weekEnd = new Date(now);
      weekEnd.setDate(weekEnd.getDate() + 7);
      const weekEndStr = localYmd(weekEnd);

      const rows: TaskRow[] = [];
      const seen = new Set<string>();

      // (a) Main row: cleaner_id + same assigned_type filter as useMyTasks("week") cleaner branch
      const { data: directRows, error: errDirect } = await supabase
        .from("cleaning_tasks")
        .select("id, property_id, scheduled_date, reservation_id")
        .eq("cleaner_id", cleaner.id)
        .eq("assigned_type", "cleaner")
        .in("status", ["scheduled", "in_progress"])
        .gte("scheduled_date", todayStr)
        .lte("scheduled_date", weekEndStr)
        .order("scheduled_date", { ascending: true })
        .order("created_at", { ascending: true });

      if (errDirect) {
        console.error("[useUpcomingCleaningTasks] direct query:", errDirect);
      } else if (directRows && directRows.length > 0) {
        const withJunction = await getTaskIdsWithChecklistAssignmentRows(directRows.map((r) => r.id));
        for (const r of directRows) {
          if (withJunction.has(r.id)) continue;
          if (seen.has(r.id)) continue;
          seen.add(r.id);
          rows.push({
            id: r.id,
            property_id: r.property_id,
            scheduled_date: r.scheduled_date,
            reservation_id: (r as { reservation_id?: string | null }).reservation_id ?? null,
          });
        }
      }

      // (b) Multi-checklist: assigned only via cleaning_task_checklist_assignments
      const { data: assignmentLinks } = await supabase
        .from("cleaning_task_checklist_assignments")
        .select("cleaning_task_id")
        .eq("cleaner_id", cleaner.id);
      const viaIds = [...new Set((assignmentLinks ?? []).map((x) => x.cleaning_task_id))];
      if (viaIds.length > 0) {
        const { data: viaRows, error: errVia } = await supabase
          .from("cleaning_tasks")
          .select("id, property_id, scheduled_date, reservation_id")
          .in("id", viaIds)
          .in("status", ["scheduled", "in_progress"])
          .gte("scheduled_date", todayStr)
          .lte("scheduled_date", weekEndStr)
          .order("scheduled_date", { ascending: true })
          .order("created_at", { ascending: true });

        if (errVia) {
          console.error("[useUpcomingCleaningTasks] via assignments:", errVia);
        } else {
          for (const r of viaRows ?? []) {
            if (seen.has(r.id)) continue;
            seen.add(r.id);
            rows.push({
              id: r.id,
              property_id: r.property_id,
              scheduled_date: r.scheduled_date,
              reservation_id: (r as { reservation_id?: string | null }).reservation_id ?? null,
            });
          }
        }
      }

      rows.sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || a.id.localeCompare(b.id));
      const limited = rows.slice(0, 10);
      const filteredTasks = excludeTaskId ? limited.filter((t) => t.id !== excludeTaskId) : limited;

      const propertyIds = [...new Set(filteredTasks.map((t) => t.property_id))];
      const reservationIds = [...new Set(filteredTasks.map((t) => t.reservation_id).filter(Boolean))] as string[];

      let guestNameByReservationId: Record<string, string> = {};
      if (reservationIds.length > 0) {
        const { data: res } = await supabase.from("reservations").select("id, guest_name").in("id", reservationIds);
        if (res) guestNameByReservationId = Object.fromEntries(res.map((r) => [r.id, r.guest_name ?? ""]));
      }

      const propertyMap = new Map<string, { name?: string; address?: string }>();
      if (propertyIds.length > 0) {
        const { data: props } = await supabase.from("properties").select("id, name, address").in("id", propertyIds);
        (props || []).forEach((p: { id: string; name?: string; address?: string }) => {
          propertyMap.set(p.id, { name: p.name, address: p.address });
        });
      }

      return filteredTasks.map((task) => {
        const property = propertyMap.get(task.property_id);
        const guest_name = task.reservation_id ? guestNameByReservationId[task.reservation_id] || null : null;
        return {
          id: task.id,
          property_id: task.property_id,
          scheduled_date: task.scheduled_date,
          property_name: property?.name?.trim() || "Property",
          property_address: property?.address ?? null,
          guest_name: guest_name || null,
        };
      }) as UpcomingCleaningTask[];
    },
    enabled: !!cleaner,
  });
};
